/**
 * SimulationEnvironment - Immersive background + ambient sound for simulation blocks.
 *
 * Detects scenario type from block config and renders:
 * - Themed CSS background (office, meeting room, customer service, etc.)
 * - Optional ambient sound (generated via Web Audio API, no external files needed)
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type EnvironmentType = 'office' | 'meeting' | 'customer-service' | 'outdoor' | 'neutral';

interface SimulationEnvironmentProps {
  blockConfig?: Record<string, any>;
  scenarioPrompt?: string;
  aiPersona?: string;
  children: React.ReactNode;
}

// Detect environment type from scenario/persona text
export function detectEnvironment(scenarioPrompt?: string, aiPersona?: string, tags?: string[]): EnvironmentType {
  const text = `${scenarioPrompt || ''} ${aiPersona || ''} ${(tags || []).join(' ')}`.toLowerCase();

  if (text.includes('müşteri') || text.includes('customer') || text.includes('şikayet') || text.includes('complaint') || text.includes('call center') || text.includes('çağrı merkezi')) {
    return 'customer-service';
  }
  if (text.includes('toplantı') || text.includes('meeting') || text.includes('konferans') || text.includes('conference') || text.includes('sunum') || text.includes('presentation')) {
    return 'meeting';
  }
  if (text.includes('dış') || text.includes('outdoor') || text.includes('saha') || text.includes('field') || text.includes('inşa') || text.includes('construction')) {
    return 'outdoor';
  }
  if (text.includes('ofis') || text.includes('office') || text.includes('çalışma') || text.includes('workplace') || text.includes('iş yeri')) {
    return 'office';
  }

  return 'neutral';
}

const environmentStyles: Record<EnvironmentType, { bg: string; label: string; icon: string }> = {
  office: {
    bg: 'linear-gradient(135deg, #e0e7ff 0%, #f0f9ff 50%, #e0f2fe 100%)',
    label: 'simulationEnvironment.office',
    icon: '🏢',
  },
  meeting: {
    bg: 'linear-gradient(135deg, #f3e8ff 0%, #fce7f3 50%, #f1f5f9 100%)',
    label: 'simulationEnvironment.meeting',
    icon: '📋',
  },
  'customer-service': {
    bg: 'linear-gradient(135deg, #fef3c7 0%, #fff7ed 50%, #fef2f2 100%)',
    label: 'simulationEnvironment.customerService',
    icon: '📞',
  },
  outdoor: {
    bg: 'linear-gradient(135deg, #d1fae5 0%, #ecfccb 50%, #fefce8 100%)',
    label: 'simulationEnvironment.outdoor',
    icon: '🏗️',
  },
  neutral: {
    bg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    label: '',
    icon: '',
  },
};

// Ambient sound generator using Web Audio API
class AmbientSoundGenerator {
  private ctx: AudioContext | null = null;
  private nodes: AudioNode[] = [];
  private gainNode: GainNode | null = null;
  private playing: boolean = false;

  private ensureContext(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const AC = window.AudioContext || (window as any).webkitAudioContext;
      if (!AC) return null;
      this.ctx = new AC();
    }
    return this.ctx;
  }

  /**
   * Generate filtered noise that sounds like ambient environment.
   */
  async start(type: EnvironmentType) {
    const ctx = this.ensureContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { /* ignore */ }
    }

    this.stop();

    this.gainNode = ctx.createGain();
    this.gainNode.gain.value = 0.08;
    this.gainNode.connect(ctx.destination);

    // Create brown noise buffer
    const bufferSize = ctx.sampleRate * 4; // 4 seconds
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      data[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = data[i];
      data[i] *= 3.5;
    }

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = buffer;
    noiseSource.loop = true;

    // Filter based on environment type
    const filter = ctx.createBiquadFilter();
    switch (type) {
      case 'office':
        filter.type = 'lowpass';
        filter.frequency.value = 400;
        break;
      case 'meeting':
        filter.type = 'lowpass';
        filter.frequency.value = 250;
        break;
      case 'customer-service':
        filter.type = 'bandpass';
        filter.frequency.value = 600;
        filter.Q.value = 0.5;
        break;
      case 'outdoor':
        filter.type = 'highpass';
        filter.frequency.value = 200;
        break;
      default:
        filter.type = 'lowpass';
        filter.frequency.value = 300;
    }

    noiseSource.connect(filter);
    filter.connect(this.gainNode);
    noiseSource.start();

    this.nodes = [noiseSource, filter, this.gainNode];
    this.playing = true;

    // For customer-service: add occasional phone ring
    if (type === 'customer-service') {
      this.schedulePhoneRing(ctx);
    }
  }

  private schedulePhoneRing(ctx: AudioContext) {
    const ring = () => {
      if (!this.playing || !this.gainNode) return;

      // Phone ring: two-tone
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const ringGain = ctx.createGain();

      osc1.frequency.value = 440;
      osc2.frequency.value = 480;
      ringGain.gain.value = 0;

      osc1.connect(ringGain);
      osc2.connect(ringGain);
      ringGain.connect(this.gainNode);

      const now = ctx.currentTime;
      // Ring pattern: 0.4s on, 0.2s off, 0.4s on, 2s off
      ringGain.gain.setValueAtTime(0, now);
      ringGain.gain.linearRampToValueAtTime(0.15, now + 0.05);
      ringGain.gain.setValueAtTime(0.15, now + 0.4);
      ringGain.gain.linearRampToValueAtTime(0, now + 0.5);
      ringGain.gain.linearRampToValueAtTime(0.15, now + 0.6);
      ringGain.gain.setValueAtTime(0.15, now + 0.9);
      ringGain.gain.linearRampToValueAtTime(0, now + 1.0);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + 1.2);
      osc2.stop(now + 1.2);

      // Schedule next ring (random 15-30s)
      setTimeout(() => ring(), 15000 + Math.random() * 15000);
    };

    setTimeout(() => ring(), 8000 + Math.random() * 10000);
  }

  stop() {
    this.nodes.forEach((node) => {
      try {
        if (node instanceof AudioScheduledSourceNode) {
          node.stop();
        }
        node.disconnect();
      } catch { /* ignore */ }
    });
    this.nodes = [];
    this.playing = false;
  }

  isPlaying(): boolean {
    return this.playing;
  }
}

const ambientGenerator = new AmbientSoundGenerator();

const SimulationEnvironment: React.FC<SimulationEnvironmentProps> = ({
  blockConfig,
  scenarioPrompt,
  aiPersona,
  children,
}) => {
  const { t } = useTranslation();
  const [ambientEnabled, setAmbientEnabled] = useState(false);
  const envType = detectEnvironment(scenarioPrompt, aiPersona, blockConfig?.tags);
  const envStyle = environmentStyles[envType];

  useEffect(() => {
    return () => {
      ambientGenerator.stop();
    };
  }, []);

  const toggleAmbient = async () => {
    if (ambientEnabled) {
      ambientGenerator.stop();
      setAmbientEnabled(false);
    } else {
      await ambientGenerator.start(envType);
      setAmbientEnabled(true);
    }
  };

  return (
    <div className="relative flex-1 flex flex-col overflow-hidden" style={{ background: envStyle.bg }}>
      {/* Decorative background pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      {/* Environment label + ambient toggle */}
      {envType !== 'neutral' && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/80 backdrop-blur-sm text-xs text-gray-600 shadow-sm">
            <span>{envStyle.icon}</span>
            <span>{t(envStyle.label)}</span>
          </div>
          <button
            onClick={toggleAmbient}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs shadow-sm transition-colors ${
              ambientEnabled
                ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                : 'bg-white/80 text-gray-600 hover:bg-white backdrop-blur-sm'
            }`}
            title={ambientEnabled
              ? t('flowInterview.ambientOff')
              : t('flowInterview.ambientOn')}
          >
            {ambientEnabled ? '🔊' : '🔈'}
            <span className="hidden sm:inline">
              {ambientEnabled
                ? t('flowInterview.ambientOnLabel')
                : t('flowInterview.ambientOffLabel')}
            </span>
          </button>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );
};

export default SimulationEnvironment;
